// crewbit-admin/src/components/money/settings/TipsToggleRow.tsx
//
// Tier 2-UI Phase E (in-app bundle) — admin parity for mobile TipsToggleRow.
// Web idiom: <input type="checkbox"> styled as switch + <button> wrapper for
// disabled-state click-through (educational toast). Optimistic UI flip +
// revert-on-error pattern per §E-inapp.D8.
//
// Body parity with mobile per feedback_admin_pair_must_match_mobile_body.md.

import { useEffect, useRef, useState } from 'react';
import { Coins } from 'lucide-react';
import { palette, radii, spacing, typography, transition } from '../../../styles/tokens';

export type TipsToggleResult = { success: true } | { success: false; message?: string };

interface TipsToggleRowProps {
  label: string;
  description: string;
  value: boolean;
  disabled: boolean;
  /** Educational copy shown when operator clicks a disabled toggle. */
  disabledMessage: string;
  onToggle: (next: boolean) => Promise<TipsToggleResult>;
  onDisabledTap?: (message: string) => void;
}

export default function TipsToggleRow({
  label,
  description,
  value,
  disabled,
  disabledMessage,
  onToggle,
  onDisabledTap,
}: TipsToggleRowProps) {
  const [localValue, setLocalValue] = useState<boolean>(value);
  const inFlightRef = useRef<boolean>(false);

  useEffect(() => {
    setLocalValue((prev) => (prev === value ? prev : value));
  }, [value]);

  const handleChange = async (next: boolean) => {
    if (disabled || inFlightRef.current) return;
    inFlightRef.current = true;
    setLocalValue(next);
    try {
      const result = await onToggle(next);
      if (!result.success) {
        setLocalValue(!next);
      }
    } catch {
      setLocalValue(!next);
    } finally {
      inFlightRef.current = false;
    }
  };

  const handleRowClick = () => {
    if (disabled && onDisabledTap) onDisabledTap(disabledMessage);
  };

  const effectiveDescription = disabled ? disabledMessage : description;

  return (
    <div
      onClick={disabled ? handleRowClick : undefined}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing.md,
        padding: spacing.md,
        cursor: disabled ? 'pointer' : 'default',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          background: disabled ? palette.g100 : palette.accentWash,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Coins size={18} color={disabled ? palette.g400 : palette.accent} strokeWidth={2} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: spacing.xs, minWidth: 0 }}>
        <span
          style={{
            fontSize: typography.size.subheading,
            fontWeight: 600,
            color: disabled ? palette.g500 : palette.g800,
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: typography.size.body,
            color: disabled ? palette.g500 : palette.g600,
            lineHeight: 1.5,
          }}
        >
          {effectiveDescription}
        </span>
      </div>
      <SwitchControl
        value={localValue}
        disabled={disabled}
        onChange={(next) => void handleChange(next)}
      />
    </div>
  );
}

interface SwitchControlProps {
  value: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}

function SwitchControl({ value, disabled, onChange }: SwitchControlProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onChange(!value);
      }}
      style={{
        position: 'relative',
        width: 44,
        height: 24,
        borderRadius: radii.full,
        border: 'none',
        background: value && !disabled ? palette.accent : palette.g200,
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0,
        opacity: disabled ? 0.6 : 1,
        transition: `background ${transition.fast}`,
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: value ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: 10,
          background: palette.white,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: `left ${transition.fast}`,
        }}
      />
    </button>
  );
}
